/**
 * VideoCall Permissions
 */
import { INVITE_CREATION_STATUSES, INVITE_TYPES, TABLE_TYPES } from "@/helpers/constants";

export default {
  computed: {
    /**
     * Check that MultiParty VideoChat is enabled in DigitalEventRoom CT
     * @return {Boolean}
     */
    multiVideoChatEnabled() {
      return this.$store.getters["videoChat/multiVideoChatEnabled"];
    },

    /**
     * Check that video chats are enabled and user give access to the camera/mic
     * @return {boolean}
     */
    videoChatFunctionalityIsActive() {
      return this.videoChatEnabled;
    },

    /**
     * Check that current user is video call initiator
     * @return {null|boolean}
     */
    amIVideoCallInitiator() {
      return (
        this.$store.state.videoChat.videoCallInitiator &&
        this.$store.state.videoChat.videoCallInitiator === this.$store.state.users.user.user_uuid
      );
    },

    /**
     * Check that user can add more people on the call
     * @return {boolean}
     */
    canAddMorePeopleOnTheVideoCall() {
      return (
        this.$store.state.videoChat.videoChatMembers.length < this.$store.getters["videoChat/maximumVideoChatMembers"]
      );
    }
  },
  methods: {
    /**
     * Check that VideoChat functionality enabled for current user
     * @param user {Object}
     * @return {boolean}
     */
    isVideoChatsEnabledForUser(user) {
      let tableUUID = user.tableUUID;
      if (!tableUUID) {
        tableUUID = this.$store.getters["users/getUser"](user.targetUserUUID).tableUUID;
      }

      let table = this.$store.getters["tables/table"](tableUUID);

      return this.videoChatFunctionalityIsActive && table && table.TABLE_TYPE !== TABLE_TYPES.PUBLIC_GALLERY;
    },

    /**
     * Show "Add user" button
     * @return {Boolean}
     */
    isAllowInvitationsEnabled() {
      return (
        this.multiVideoChatEnabled &&
        (this.isConnectingToOneToOneVideoChatInProgress() ||
          (!this.isConnectingToOneToOneVideoChatInProgress() &&
            this.$store.state.videoChat.activeVideoChat &&
            (this.amIVideoCallInitiator || this.$store.state.videoChat.activeVideoChat.allowInvitations)))
      );
    },

    /**
     * Show "Allow invitations" checkbox - this will be available only for call initiator
     * @return {boolean|boolean|*|null}
     */
    isAllowInvitationsCheckboxVisible() {
      return (
        this.multiVideoChatEnabled &&
        (this.isConnectingToOneToOneVideoChatInProgress() ||
          (!this.isConnectingToOneToOneVideoChatInProgress() &&
            this.$store.state.videoChat.activeVideoChat &&
            this.amIVideoCallInitiator))
      );
    },

    /**
     * Check that connect to One-To-One Video Chat in progress
     * @return {boolean|boolean}
     */
    isConnectingToOneToOneVideoChatInProgress() {
      return (
        (!this.$store.state.videoChat.activeVideoChat &&
          !this.$store.state.videoChat.activeVideoSession &&
          this.$store.getters["invites/sentVideoCallInvites"].length) ||
        (this.$store.state.videoChat.activeVideoChat &&
          this.$store.state.videoChat.activeVideoSession &&
          this.$store.state.videoChat.connectionsCount === 1 &&
          this.$store.getters["invites/sentVideoCallInvites"].length)
      );
    },

    /**
     * Check that user can send video call invites
     * @param user {Object}
     * @return {boolean}
     */
    canUserSendVideoCallInvites(user) {
      return (
        // Check that we already don't have ant VideoCall invites in "creation" state
        this.$store.state.invites.videoCallInviteCreationStatus !== INVITE_CREATION_STATUSES.IN_PROCESS &&
        // Check that we can add more people on the video call
        this.canAddMorePeopleOnTheVideoCall &&
        // Checking global room config and webcam accessible
        this.videoChatFunctionalityIsActive &&
        // Check that user can send videoCall invite
        this.canUserInviteNewUserToVideoCall() &&
        // Check that target user available for video call
        this.$store.getters["users/canUserAcceptVideoCallInvites"](user) &&
        // Check that we didn't already send videoCall invite to this user
        !this.$store.getters["invites/hasUserProcessingInvites"](INVITE_TYPES.VIDEO_CALL, user.userUUID) &&
        // Check that we don't have current videoCall with this user
        !this.$store.getters["videoChat/videoChatMembers"].find(member => member.userUUID === user.userUUID)
      );
    },

    /**
     * Check that user can invite new user to the video call
     * @return {boolean|null|boolean|*}
     */
    canUserInviteNewUserToVideoCall() {
      let activeVideoChat = this.$store.state.videoChat.activeVideoChat;
      let invitesCount = this.$store.getters["invites/sentVideoCallInvites"].length;

      if (this.multiVideoChatEnabled) {
        if (activeVideoChat) {
          return this.amIVideoCallInitiator || activeVideoChat.allowInvitations;
        } else {
          return this.amIVideoCallInitiator || invitesCount === 0;
        }
      } else {
        if (activeVideoChat) {
          return false;
        } else {
          return invitesCount === 0;
        }
      }
    }
  }
};
